<script setup lang="ts">
  import { toRef } from 'vue';
  import { Loader2 } from 'lucide-vue-next';
  import { DefaultTable } from '@/shared/ui';
  import { pluralize } from '@/shared/lib';
  import type { TrainingCollaboratorPosition } from '@/entities/Training';
  import { useTrainingControlCollaborators } from '../../model/useTrainingControlCollaborators';
  import { formatTrainingControlValue } from '../../lib/formatTrainingControlValue';

  const props = defineProps<{
    trainingId: number;
  }>();

  const { collaborators, loading, error, count } = useTrainingControlCollaborators(
    toRef(props, 'trainingId')
  );

  const HEADERS = ['Colaboradores', 'Matrícula', 'Cargo'];

  function positionsLabel(positions: TrainingCollaboratorPosition[]): string {
    if (positions.length === 0) return formatTrainingControlValue(null);
    return positions.map((position) => position.name).join(', ');
  }
</script>

<template>
  <section
    data-testid="training-control-section-collaborators"
    class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
  >
    <div class="flex flex-col gap-1">
      <h2 class="text-sm font-semibold text-gray-07">
        Colaboradores selecionados para o treinamento
      </h2>
      <span
        v-if="!loading"
        data-testid="training-control-collaborators-count"
        class="text-xs text-gray-05"
      >
        Treinamento com {{ count }} {{ pluralize(count, 'colaborador', 'colaboradores') }}
      </span>
    </div>

    <div
      v-if="loading"
      data-testid="training-control-collaborators-loading"
      class="flex justify-center py-4"
    >
      <Loader2 class="h-5 w-5 animate-spin text-[var(--theme-secondary-ink)]" />
    </div>

    <div
      v-else-if="error"
      data-testid="training-control-collaborators-error"
      class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
    >
      {{ error }}
    </div>

    <DefaultTable
      v-else
      data-testid="training-control-collaborators-table"
    >
      <thead class="bg-white-01">
        <tr>
          <th
            v-for="header in HEADERS"
            :key="header"
            class="px-4 py-3 text-left text-xs font-semibold text-gray-07"
          >
            {{ header }}
          </th>
        </tr>
      </thead>
      <tbody class="border-t border-gray-02">
        <tr
          v-for="collaborator in collaborators"
          :key="collaborator.id"
          data-testid="training-control-collaborator-row"
          class="border-b border-gray-02 last:border-b-0"
        >
          <td class="px-4 py-3 text-sm text-gray-07">
            {{ collaborator.name }}
          </td>
          <td class="px-4 py-3 text-sm text-gray-07">
            {{ collaborator.registrationNumber }}
          </td>
          <td class="px-4 py-3 text-sm text-gray-07">
            {{ positionsLabel(collaborator.positions) }}
          </td>
        </tr>
        <tr v-if="collaborators.length === 0">
          <td
            :colspan="HEADERS.length"
            data-testid="training-control-collaborators-empty"
            class="px-4 py-6 text-center text-sm text-gray-05"
          >
            Nenhum colaborador vinculado ao treinamento
          </td>
        </tr>
      </tbody>
    </DefaultTable>
  </section>
</template>
